/**
 * src/components/settlement/SettleUpModal.tsx
 * Ultra-Premium Google Pay Settle Up & Payout Sheet
 * Exclusively supports Google Pay (Tez) and Google Pay dynamic vector QR.
 */

import React, { useState, useEffect } from 'react';
import { GlassModal } from '../ui/GlassModal';
import { generateQrMatrix, generateUpiUrl } from '../../utils/qrCode';
import { 
  QrCode, 
  Copy, 
  Check, 
  Smartphone, 
  ArrowUpRight, 
  UploadCloud, 
  ShieldCheck, 
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { haptic, playTapSound, playSuccessSound } from '../../utils/audioHaptics';

export const HARDCODED_ADMIN_UPI = 'chintankheradia123-1@okaxis';

interface SettleUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  amountOwed: number; // positive number representing the amount to settle
  adminUpiId?: string | null;
  userUpiId?: string | null;
  isPayout?: boolean; // true if admin is paying out to member, false if member is settling to admin
  onSettlementSubmitted?: (data: {
    user_id: number;
    amount: number;
    receipt_url?: string;
    description: string;
  }) => Promise<void>;
}

export const SettleUpModal: React.FC<SettleUpModalProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  amountOwed,
  adminUpiId,
  userUpiId,
  isPayout = false,
  onSettlementSubmitted
}) => {
  const { success, error: toastError } = useToast();

  const [copied, setCopied] = useState(false);
  const [receiptBase64, setReceiptBase64] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'qr' | 'gpay'>('qr');
  const [customUpiInput, setCustomUpiInput] = useState('');

  // Determine receiving UPI ID:
  // If isPayout (admin paying member): recipient is member's upi_id (or custom input fallback)
  // If normal settlement (member paying admin): recipient is admin's upi_id or hardcoded default
  const resolvedAdminUpi = (adminUpiId && adminUpiId.trim()) ? adminUpiId.trim() : HARDCODED_ADMIN_UPI;
  const initialRecipientUpi = isPayout 
    ? ((userUpiId && userUpiId.trim()) ? userUpiId.trim() : '')
    : resolvedAdminUpi;

  useEffect(() => {
    setCustomUpiInput(initialRecipientUpi);
    setReceiptBase64(null);
    setCopied(false);
  }, [initialRecipientUpi, isOpen]);

  const cleanAmount = Math.abs(amountOwed);
  const activeRecipientUpi = customUpiInput.trim() || initialRecipientUpi || resolvedAdminUpi;

  // Generate standard UPI URL targeting Google Pay
  const upiUrl = generateUpiUrl({
    pa: activeRecipientUpi,
    pn: isPayout ? memberName : 'CKAudit Ledger',
    am: cleanAmount,
    tn: isPayout ? `Payout for ${memberName}` : `Settlement for ${memberName}`
  });

  const qrMatrix = generateQrMatrix(upiUrl);

  const handleCopyUpi = () => {
    haptic.tap();
    playTapSound();
    navigator.clipboard.writeText(activeRecipientUpi);
    setCopied(true);
    success(`Google Pay UPI ID copied: ${activeRecipientUpi}`, 'Copied to Clipboard');
    setTimeout(() => setCopied(false), 2200);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toastError('Receipt image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setReceiptBase64(reader.result as string);
      haptic.success();
      playSuccessSound();
      success('Payment screenshot attached successfully!', 'Receipt Ready');
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmSettlement = async () => {
    if (!onSettlementSubmitted) return;

    try {
      setIsSubmitting(true);
      haptic.button();
      playTapSound();
      await onSettlementSubmitted({
        user_id: memberId,
        amount: cleanAmount,
        receipt_url: receiptBase64 || undefined,
        description: isPayout 
          ? `Google Pay settlement payout to ${memberName} (${activeRecipientUpi})`
          : `Google Pay settlement payment by ${memberName} to ${activeRecipientUpi}`
      });
      haptic.success();
      playSuccessSound();
      success(
        isPayout
          ? `Payout settlement of ₹${cleanAmount.toFixed(2)} submitted for review.`
          : `Payment settlement of ₹${cleanAmount.toFixed(2)} submitted! It is now Under Review.`,
        'Under Review'
      );
      onClose();
    } catch (err: any) {
      toastError(err.message || 'Failed to submit settlement');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google Pay Deep Links
  const gpayDeepLink = `tez://upi/pay?pa=${encodeURIComponent(activeRecipientUpi)}&pn=${encodeURIComponent(isPayout ? memberName : 'CKAudit')}&am=${cleanAmount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(isPayout ? `Payout for ${memberName}` : `Settlement for ${memberName}`)}`;
  const genericUpiLink = `upi://pay?pa=${encodeURIComponent(activeRecipientUpi)}&pn=${encodeURIComponent(isPayout ? memberName : 'CKAudit')}&am=${cleanAmount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(isPayout ? `Payout for ${memberName}` : `Settlement for ${memberName}`)}`;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={isPayout ? "Google Pay Settlement Payout" : "Google Pay Settle Up"}
      maxWidth="480px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Settlement Summary Header Card */}
        <div
          className="apple-card"
          style={{
            padding: '22px 20px',
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative'
          }}
        >
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-tertiary)',
              marginBottom: '4px'
            }}
          >
            {isPayout ? 'Member Credit Settlement Payout' : 'Outstanding Settlement Balance'}
          </div>

          <div
            style={{
              fontSize: '2.4rem',
              fontWeight: 800,
              color: isPayout ? 'var(--ios-green)' : 'var(--ios-red)',
              letterSpacing: '-0.04em',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.1
            }}
          >
            ₹{cleanAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>

          <div
            style={{
              fontSize: '0.84rem',
              color: 'var(--text-secondary)',
              marginTop: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {isPayout ? (
              <>
                <span>Transfer To:</span>
                <strong style={{ color: 'var(--ios-green)' }}>{memberName}</strong>
              </>
            ) : (
              <>
                <span>Payee:</span>
                <strong style={{ color: 'var(--text-primary)' }}>{memberName}</strong>
                <span>→ Admin</span>
              </>
            )}
          </div>

          {/* Google Pay Exclusive Badge */}
          <div
            style={{
              marginTop: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 10px',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(66, 133, 244, 0.12)',
              border: '0.5px solid rgba(66, 133, 244, 0.3)',
              color: '#4285f4',
              fontSize: '0.72rem',
              fontWeight: 700
            }}
          >
            <span>Exclusive Mode: Google Pay Only</span>
          </div>
        </div>

        {/* Missing Member UPI Warning if Payout */}
        {isPayout && !userUpiId && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: '14px',
              background: 'rgba(255, 159, 10, 0.12)',
              border: '1px solid rgba(255, 159, 10, 0.28)',
              color: '#ff9f0a',
              fontSize: '0.78rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
              <AlertCircle size={15} />
              <span>{memberName} has not configured their Google Pay UPI ID yet</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
              Enter their UPI ID below to generate the Google Pay QR code or send payment directly:
            </div>
            <input
              type="text"
              placeholder="e.g. username@okaxis or 9876543210@okhdfcbank"
              value={customUpiInput}
              onChange={(e) => setCustomUpiInput(e.target.value)}
              style={{
                marginTop: '4px',
                padding: '8px 12px',
                borderRadius: '8px',
                background: 'var(--surface-primary)',
                border: '1px solid rgba(255, 159, 10, 0.4)',
                color: 'var(--text-primary)',
                fontSize: '0.82rem',
                outline: 'none'
              }}
            />
          </div>
        )}

        {/* Segmented Tab Switcher: QR Code vs Google Pay One-Tap */}
        <div
          className="ios-segmented-control"
          style={{ width: '100%', padding: '4px' }}
        >
          <button
            type="button"
            onClick={() => {
              haptic.selection();
              playTapSound();
              setActiveTab('qr');
            }}
            className={`ios-segment-button ${activeTab === 'qr' ? 'active' : ''}`}
            style={{ flex: 1, padding: '8px 12px', fontSize: '0.84rem' }}
          >
            <span>Google Pay QR Code</span>
          </button>
          <button
            type="button"
            onClick={() => {
              haptic.selection();
              playTapSound();
              setActiveTab('gpay');
            }}
            className={`ios-segment-button ${activeTab === 'gpay' ? 'active' : ''}`}
            style={{ flex: 1, padding: '8px 12px', fontSize: '0.84rem' }}
          >
            <span>Launch Google Pay</span>
          </button>
        </div>

        {activeTab === 'qr' ? (
          /* Tab 1: Vector QR Code Display */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: '210px',
                height: '210px',
                borderRadius: '24px',
                background: '#ffffff',
                padding: '14px',
                boxShadow: '0 12px 36px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg
                viewBox="0 0 25 25"
                style={{ width: '100%', height: '100%', shapeRendering: 'crispEdges' }}
              >
                {qrMatrix.map((row, r) =>
                  row.map((filled, c) => (
                    filled ? (
                      <rect
                        key={`${r}-${c}`}
                        x={c}
                        y={r}
                        width={1}
                        height={1}
                        fill="#000000"
                      />
                    ) : null
                  ))
                )}
              </svg>

              {/* Central Google Pay Logo Badge */}
              <div
                style={{
                  position: 'absolute',
                  padding: '4px 8px',
                  borderRadius: '10px',
                  background: '#ffffff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid rgba(0,0,0,0.08)',
                  gap: '3px'
                }}
              >
                <span style={{ fontSize: '0.82rem', fontWeight: 900, color: '#4285F4' }}>G</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#5f6368' }}>Pay</span>
              </div>
            </div>

            {/* UPI ID Pill & Copy Button */}
            <div
              onClick={handleCopyUpi}
              className="ios-tap-target"
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'var(--border-hairline)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Google Pay UPI:</span>
              <strong style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>{activeRecipientUpi}</strong>
              {copied ? (
                <Check size={14} color="var(--ios-green)" />
              ) : (
                <Copy size={14} color="var(--ios-blue)" />
              )}
            </div>

            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '8px' }}>
              Open Google Pay on your phone and scan to settle instantly
            </div>
          </div>
        ) : (
          /* Tab 2: One-Tap Google Pay Launcher */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Primary Google Pay Tez Card */}
            <a
              href={gpayDeepLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                haptic.button();
                playTapSound();
              }}
              className="ios-list-row ios-tap-target"
              style={{
                padding: '16px 20px',
                borderRadius: '18px',
                background: 'linear-gradient(135deg, rgba(66, 133, 244, 0.12) 0%, rgba(52, 168, 83, 0.08) 100%)',
                border: '1.5px solid rgba(66, 133, 244, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textDecoration: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    flexShrink: 0
                  }}
                >
                  <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#4285F4' }}>G</span>
                  <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#5f6368' }}>Pay</span>
                </div>
                <div>
                  <div style={{ fontSize: '0.98rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    Open Google Pay (Tez)
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Pay ₹{cleanAmount.toFixed(2)} to {activeRecipientUpi}
                  </div>
                </div>
              </div>
              <ArrowUpRight size={20} color="#4285F4" />
            </a>

            {/* Fallback Universal UPI Link (For Android/iOS intent handlers) */}
            <a
              href={genericUpiLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                haptic.tap();
                playTapSound();
              }}
              className="ios-list-row ios-tap-target"
              style={{
                padding: '12px 18px',
                borderRadius: '14px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: 'var(--border-hairline)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textDecoration: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Smartphone size={18} color="var(--text-secondary)" />
                <span style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}>
                  Alternative: Open UPI Intent Selector
                </span>
              </div>
              <ArrowUpRight size={16} color="var(--text-tertiary)" />
            </a>

            <div style={{ 
              fontSize: '0.72rem', 
              color: 'var(--text-tertiary)', 
              textAlign: 'center',
              padding: '4px'
            }}>
              ✓ Other payment apps (PhonePe, Paytm, BHIM) removed per system configuration.
            </div>
          </div>
        )}

        {/* Attachment & Verification Section */}
        <div className="ios-inset-group" style={{ margin: 0, padding: '16px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            Attach Google Pay Payment Screenshot (Optional)
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px dashed var(--border-subtle)',
              cursor: 'pointer'
            }}
            className="ios-tap-target"
          >
            <UploadCloud size={20} color="var(--ios-blue)" />
            <span style={{ fontSize: '0.84rem', color: receiptBase64 ? 'var(--ios-green)' : 'var(--text-secondary)', fontWeight: 500 }}>
              {receiptBase64 ? 'Screenshot attached ✓' : 'Upload payment screenshot...'}
            </span>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </label>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              haptic.tap();
              playTapSound();
              onClose();
            }}
            className="ios-tap-target"
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: 'var(--radius-pill)',
              border: 'var(--border-hairline)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: '0.92rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Close
          </button>

          <button
            type="button"
            onClick={handleConfirmSettlement}
            disabled={isSubmitting}
            className="ios-primary-button ios-tap-target"
            style={{
              flex: 2,
              padding: '14px',
              fontSize: '0.92rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? (
              <span>Submitting for Review...</span>
            ) : (
              <>
                <ShieldCheck size={18} />
                <span>{isPayout ? 'Confirm Payout Settled' : 'Notify Admin of Payment'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </GlassModal>
  );
};
